import React, { FC, useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import SectionWrapper from './LockedSectionWrapper'

interface EmbedCodeSectionProps {
  videoId: string
  videoName: string
  thumbnailUrl?: string
  baseUrl: string
}

// Square presets — the quadrant video and its thumbnails are square.
const PRESETS = [
  { label: '480 x 480', w: 480, h: 480 },
  { label: '640 x 640', w: 640, h: 640 },
  { label: '960 x 960', w: 960, h: 960 },
]

const EmbedCodeSection: FC<EmbedCodeSectionProps> = ({
  videoId,
  videoName,
  thumbnailUrl,
  baseUrl,
}) => {
  const [width, setWidth] = useState(640)
  const [height, setHeight] = useState(640)
  const [copied, setCopied] = useState(false)

  const embedUrl = `${baseUrl}/embed/${videoId}`
  const embedCode = `<iframe src="${embedUrl}" width="${width}" height="${height}" frameborder="0" allow="gyroscope; accelerometer; fullscreen" allowfullscreen style="border:0" title="${videoName}"></iframe>`

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [embedCode])

  const thumbnailImageUrl = thumbnailUrl
    ? `${baseUrl}/api/v1/public/videos/${videoId}/thumbnail/image?v=${encodeURIComponent(thumbnailUrl)}`
    : undefined

  return (
    <SectionWrapper title="Embed" status="complete">
      {thumbnailImageUrl && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Thumbnail</Typography>
          <img
            src={thumbnailImageUrl}
            alt={`${videoName} thumbnail`}
            style={{ maxWidth: 320, borderRadius: 4, display: 'block' }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            size="small"
            variant={width === p.w && height === p.h ? 'contained' : 'outlined'}
            onClick={() => { setWidth(p.w); setHeight(p.h) }}
          >
            {p.label}
          </Button>
        ))}
        <TextField
          size="small"
          label="W"
          type="number"
          value={width}
          onChange={(e) => setWidth(Math.max(1, parseInt(e.target.value, 10) || 0))}
          sx={{ width: 80 }}
        />
        <Typography variant="body2" color="text.secondary">x</Typography>
        <TextField
          size="small"
          label="H"
          type="number"
          value={height}
          onChange={(e) => setHeight(Math.max(1, parseInt(e.target.value, 10) || 0))}
          sx={{ width: 80 }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          bgcolor: '#1a1a1a',
          borderRadius: 1,
          p: 2,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          color: '#ccc',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {embedCode}
        <Button
          size="small"
          variant="contained"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
    </SectionWrapper>
  )
}

export default EmbedCodeSection
